import { FormStyle, getLocaleDayNames, getLocaleMonthNames, TitleCasePipe, TranslationWidth } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from "@angular/core";
import { InterpolatableTranslationObject, TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'ngx-webstorage';
import { ImportTranslationFileRequest } from '../actions/admin/components/translations/import-translation-modal/import-translation-file-request';
import { EnumTraslationKeysEnum } from "../models/enums/enum-traslation-keys-enum";
import { TranslationSchema } from "../models/translations";
import { ApiHttpClient } from '../modules/tcm-core.module';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private titleCasePipe: TitleCasePipe,
    private translateService: TranslateService,
    private readonly httpClient: ApiHttpClient,
    private readonly localStorageService: LocalStorageService) {
  }
  // ANGULAR UPGRADE BREAKING CHANGE
  translate(executor: (schema: InterpolatableTranslationObject) => any) {

    let result = null;

    if (!this.getTranslationMode()) {
      try {

        const currentTranslations = this.translateService.translations[this.translateService.currentLang];

        result = executor(currentTranslations);
      }
      catch (e) {
        console.error(e);
      }
    }

    if (!result) {
      let stringPredicate = executor.toString();
      stringPredicate = stringPredicate.substring(stringPredicate.indexOf('.') + 1).trim();
      return stringPredicate.substring(0, stringPredicate.indexOf(';')).trim()
    }

    return result;
  }

  getDayShortNames(): string[] {
    return getLocaleDayNames(this.locale, FormStyle.Standalone, TranslationWidth.Short).map(x => this.titleCasePipe.transform(x));
  }

  getDayFullNames(): string[] {
    return getLocaleDayNames(this.locale, FormStyle.Standalone, TranslationWidth.Wide).map(x => this.titleCasePipe.transform(x));
  }

  getMonthShortNames(): string[] {
    return getLocaleMonthNames(this.locale, FormStyle.Standalone, TranslationWidth.Short).map(x => this.titleCasePipe.transform(x));
  }

  getMonthFullNames(): string[] {
    return getLocaleMonthNames(this.locale, FormStyle.Standalone, TranslationWidth.Wide).map(x => this.titleCasePipe.transform(x));
  }

  getApiTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ApiEnum);
  }

  getAppointmentStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.AppointmentStatusEnum);
  }

  getAppointmentTypeGroupTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.AppointmentTypeGroupEnum);
  }
  getSubscriptionTypeGroupTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SubscriptionTypeGroupEnum);
  }

  getAuthorizationTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.AuthorizationTypeEnum);
  }

  getAssignedToPatientThroughTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.AssignedToPatientThroughEnum);
  }

  getAppriseApiCallTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.AppriseApiCallTypeEnum);
  }

  getAppriseApiCallSubTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.AppriseApiCallSubTypeEnum);
  }

  getAppriseApiCallModeTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.AppriseApiCallModeTypeEnum);
  }

  getDefaultPageTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.DefaultPageEnum);
  }

  getDocumentTemplateTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.DocumentTemplateTypeEnum);
  }

  getDocumentTemplateEmailSourceTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.DocumentTemplateEmailSourceEnum);
  }

  getDocumentTypeGroupTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.DocumentTypeGroupEnum);
  }

  getClaimExcelTokenTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimExcelTokenEnum);
  }

  getClaimUnbundleCodeActionTranslations() {
    return this.getEnumTranslation(EnumTraslationKeysEnum.ClaimUnbundleCodeActionEnum);
  }

  getClaimBatchStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimBatchStatusEnum);
  }

  getSchedulingClaimFrequencyTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SchedulingClaimFrequencyEnum);
  }

  getClaimStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimStatusEnum);
  }

  getEligibilityRequestTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.EligibilityRequestTypeEnum);
  }

  getClaimInsurancePlanRankTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimInsurancePlanRankEnum);
  }

  getClaimInsurancePlanTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimInsurancePlanTypeEnum);
  }

  getClaimsGroupFilterTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimsGroupFilterEnum);
  }

  getClaimInsuredBenefitAssignmentCertificationTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimInsuredBenefitAssignmentCertificationEnum);
  }

  getClaimInsuredProviderAcceptAssignmentTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimInsuredProviderAcceptAssignmentEnum);
  }

  getClaimInsuredReleaseInformationCodeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimInsuredReleaseInformationCodeEnum);
  }

  getClaimInsuredSubmitterTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimInsuredSubmitterTypeEnum);
  }

  getCommonReportTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.CommonReportTypeEnum);
  }

  getCommunicationSourceTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.CommunicationSourceEnum);
  }

  getServiceEnumTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ServiceTypeEnum);
  }

  getServiceCategoryTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ServiceCategoryEnum);
  }

  getServiceStatusEnumTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ServiceStatusEnum);
  }

  getPatientDocumentStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PatientDocumentStatusEnum);
  }

  getEarSideTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.EarSideEnum);
  }

  getEarSetupTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.EarSetupTypeEnum);
  }

  getEdiModeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.EdiModeEnum);
  }

  getIcdCodesTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.IcdCodePositionEnum);
  }

  getInsurancePriorityTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.InsurancePriorityEnum);
  }

  getInsuranceProductTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.InsuranceProductTypeEnum);
  }

  getInsurancePlanFilingMethodTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.InsurancePlanFilingMethodEnum);
  }

  getLicensePlanTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.LicensePlanTypeEnum);
  }

  getLookupTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.LookupTypeEnum);
  }

  getNoteStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.NoteStatusEnum);
  }

  getNoteTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.NoteTypeEnum);
  }

  getOrderLineStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.OrderLineStatusEnum);
  }

  getOrderStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.OrderStatusEnum);
  }

  getSchedulerDefaultViewTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SchedulerDefaultViewEnum);
  }

    getSalesOrderInventoryStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SalesOrderInventoryStatusEnum);
  }

  getSalesOrderSearchSourceFilterTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SalesOrderSearchSourceFilterEnum);
  }

  getSalesOrderSearchTypeFilterTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SalesOrderSearchTypeFilterEnum);
  }

  getPatientInsuranceCodeTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PatientInsuranceCodeTypeEnum);
  }

  getPatientOrderStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PatientOrderStatusEnum);
  }

  getPatientPreferredContactTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PatientPreferredContactEnum);
  }

  getPatientStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PatientStatusEnum);
  }

  getPaymentMethodTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PaymentMethodEnum);
  }

  getTransactionTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.TransactionTypeEnum);
  }
  
  getEntryModeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.EntryModeIdEnum);
  }

  getEndOfDayStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.EndOfDayStatusEnum);
  }

  getPaymentTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PaymentTypeEnum);
  }

  getPaymentStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PaymentStatusEnum);
  }

  getProductCategoryRankTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ProductCategoryRankEnum);
  }
  getRepairTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.RepairTypeEnum);
  }

  getProductKitTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ProductKitTypeEnum);
  }

  getProductBundleTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ProductBundleTypeEnum);
  }

  getPatientReferringPhysicianPriorityTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PatientReferringPhysicianPriorityEnum);
  }

  getPureToneCategoryTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PureToneCategoryEnum);
  }

  getPureToneDegreeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PureToneDegreeEnum);
  }

  getPureToneFrequencyModifierTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PureToneFrequencyModifierEnum);
  }

  getProcessingInfoMessageTypeEnumTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ProcessingInfoMessageType);
  }

  getSalesPersonModeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SalesPersonModeEnum);
  }

  getSpeechSATTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SpeechSATEnum);
  }

  getSalesOrderClaimProcessingStatusEnumTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SalesOrderClaimProcessingStatusEnum);
  }

  getClaimProcessingExecuteSourceEnumTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ClaimProcessingExecuteSourceTypeEnum);
  }

  getSpeechWordRecognitionTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SpeechWordRecognitionEnum);
  }

  getSpeechSRTTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SpeechSRTEnum);
  }

  getImmMidEarReflexTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ImmMidEarReflexEnum);
  }

  getRecommendationSideTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.RecommendationSideEnum);
  }

  getSideExtendedTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.EarSideExtendedUnspecifiedEnum);
  }

  getRecommendationDurationTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.RecommendationDurationEnum);
  }

  getRecommendationEquilibriumTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.RecommendationEquilibriumType);
  }

  getMedicalReportRecommendationTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.MedicalReportRecommendationEnum);
  }

  getAppointmentRecommendationTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.AppointmentRecommendationEnum);
  }

  getQueueStatusTranslations(){
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.QueueStatusEnum);
  }

  getRepairLocationTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.RepairLocationEnum);
  }
  getRequireMemberIdTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.MemberIdRequiredTypeEnum);
  }

  getMemberIdFormatTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.MemberIdFormatEnum);
  }

  getReportTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ReportTypeEnum);
  }

  getLossAndDamageOptionTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.LossAndDamageOptionEnum);
  }

  getReturnOrderLineOptionTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ReturnOrderLineOptionEnum);
  }

  getSerializedItemStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SerializedItemStatusEnum);
  }

  getWarrantyPriorityTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.WarrantyPriorityEnum);
  }

  getBenefitTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.BenefitTypeEnum);
  }

  getSubmitterTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SubmitterTypeEnum);
  }

  getStateAdministrativeLevelTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.StateAdministrativeLevelEnum);
  }

  getSsrsFilterTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SsrsReportFilterTypeEnum);
  }

  getTaxIdTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.TaxIdTypeEnum);
  }

  getTokenConditionTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.TokenConditionEnum);
  }

  getWorkQueueTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.WorkQueueTypeEnum);
  }

  getInventoryOrderStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.InventoryOrderStatusEnum);
  }

  getInventoryOrderLineStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.InventoryOrderLineStatusEnum);
  }

  getImportEntityTypeTranslations() {
    return this.getEnumTranslation(EnumTraslationKeysEnum.ImportEntityTypeEnum);
  }

  getImportCsvFileDelimiterTranslations() {
    return this.getEnumTranslation(EnumTraslationKeysEnum.ImportCsvFileDelimiterEnum);
  }

  getImportValueErrorTranslations() {
    return this.getEnumTranslation(EnumTraslationKeysEnum.ImportValueErrorEnum);
  }

  getTimeSlotTypeTranslations() {
    return this.getEnumTranslation(EnumTraslationKeysEnum.TimeSlotTypeEnum);
  }

  getTemplateSlotTypeTranslations() {
    return this.getEnumTranslation(EnumTraslationKeysEnum.TemplateSlotTypeEnum);
  }

  getUnavailableSlotApplyToTypeTranslations() {
    return this.getEnumTranslation(EnumTraslationKeysEnum.UnavailableSlotApplyToTypeEnum);
  }

  getUnitWeightBasisTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.UnitWeightBasisEnum);
  }

  getSACLevelTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SACLevelEnum);
  }

  getYearPurchasedTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.YearPurchasedEnum);
  }

  getPatientSatisfactionTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PatientSatisfactionEnum);
  }

  getMedicalReportConfirmationTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.MedicalReportConfirmationEnum);
  }

  getMedicalReportBinauralDiscriminationTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.MedicalReportBinauralDiscriminationEnum);
  }

  getPureToneStimulusMethodTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PureToneStimulusMethodEnum);
  }

  getMedicalReportTympTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ImmMidEarEvalTympEnum);
  }

  getMedicalReportOutcomeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.MedicalReportOutcomEnum);
  }

  getMedicalReportReturnTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.MedicalReportReturnEnum);
  }

  getMedicalReportLevelTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.MedicalReportLevelEnum);
  }

  getRecommendationSideFullNameEnumTranslations() {
    return this.getEnumTranslationInternal('RecommendationSideFullNameEnum');
  }

  getPurposeOfVisitTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.PurposeOfVisitEnum);
  }

  getDispensingPlanModeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.DispensingPlanModeEnum);
  }

  getEnumTranslation(enumTranslationKey: EnumTraslationKeysEnum): { [key: string]: string } {
    return this.getEnumTranslationInternal(enumTranslationKey);
  }

  getApiRequestTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ApiRequestTypeEnum);
  }

  getRequestSourceTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.RequestSourceEnum);
  }

  getErpSystemTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ErpSystemEnum);
  }
  getTaxModeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.TaxModeEnum);
  }

  getBusinessCentralApiCallTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.BusinessCentralApiCallTypeEnum);
  }

  getBusinessCentralAuthenticationTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.BusinessCentralAuthenticationTypeEnum);
  }

  getAppriseApiCallTestTypeTranslations(){
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.AppriseRequestTestTypeEnum);
  }

  getInventoryOrdeTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.InventoryOrderTypeEnum);
  }

  getRecallStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.RecallStatusEnum);
  }

  private getEnumTranslationInternal(enumTranslationKey: EnumTraslationKeysEnum | string) {
    return this.translateService.translations[this.translateService.currentLang]["Enumerations"][enumTranslationKey];
  }

  getSystemDefaultTranslationsFile() {
    return this.httpClient.getFileAsync('Translations/GetSystemDefaultTranslationsFile');
  }

  getUserTranslationsFile(translationId: any) {
    return this.httpClient.getFileAsync('Translations/GetUserTranslationsFile?translationId='+ translationId);
  }

  importFileAsync(request: ImportTranslationFileRequest, file: File, cancellationToken: Promise<any>): Promise<Boolean> {
    return this.httpClient.postFileAsync<Boolean>('Translations/ImportTranslationsFile', file, request, cancellationToken);
  }

  getTranslationMode() {
    return this.localStorageService.retrieve("isTranslationMode") || false;
  }

  getIntegrationStatusTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.IntegrationStatusEnum);
  }

  getIntegrationStatusOptions() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.OrderStatusEnum);
  }

  getProductStyleSideOptions() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ProductStyleSideEnum);
  }

  getExternalApiCallTypeTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ExternalApiCallTypeEnum);
  }

  getServicePlanPriorityTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.InsurancePriorityEnum);
  }

  getSqlTaskStatusEnumTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.SqlTaskStatusEnum);
  }

  getCreditCardPayTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.CreditCardPayEnum);
  }

  getAdpPersonSignatureOptions() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.ADPPersonEnum);
  }

  getEndOfDayEnforceUserPriorityTranslations() {
    return this.getEnumTranslationInternal(EnumTraslationKeysEnum.EndOfDayEnforceUserPriorityEnum);
  }
}
